import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Check } from 'lucide-react';
import {
  ButtonContainer,
  CaptureButton,
  CheckIcon,
  CloseButton,
  CompleteButton,
  CompletedItem,
  CompletedItemText,
  CompletedList,
  CurrentOverlaySection,
  ErrorMessage,
  MapContainer,
  ModalBody,
  ModalContainer,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  OverlayLabel,
  OverlayName,
  ProgressBar,
  ProgressFill,
  ProgressSection,
  ProgressText,
  Sidebar,
  SuccessMessage,
} from './modal.style';
import ModalLoadingScreen from '../../../../components/loadingScreen/modalLoading';
import { getOverlayByAddressCoordinates } from '../../../../services/getAddressList';
import { ArcgisMap } from '../../../pages/mapComponent';
import { generateReport } from '../Overlayreportgenerator';

const NewMultipleScreenshotModal = ({
  isOpen,
  onClose,
  searchAddress,
  overlayOptions = [],
  onComplete,
  geoJsonCode,
}) => {
  const [currentOverlayData, setCurrentOverlayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [capturedScreenshots, setCapturedScreenshots] = useState({});

  const captureScreenshotFn = useRef(null);

  const totalOverlays = overlayOptions.length;
  const capturedCount = Object.keys(capturedScreenshots).length;
  const progress = totalOverlays > 0 ? (capturedCount / totalOverlays) * 100 : 0;
  const allCompleted = capturedCount === totalOverlays && totalOverlays > 0;

  const availableOverlays = overlayOptions.filter(
    (overlay) => !capturedScreenshots[overlay.overlayName]
  );

  const currentOverlay = availableOverlays.length > 0 ? availableOverlays[0] : null;

  const clearAllMemory = useCallback(() => {
    setCapturedScreenshots({});
    setCurrentOverlayData(null);
    setError('');
    setSuccessMessage('');
    captureScreenshotFn.current = null;
  }, []);

  // 🔥 FETCH OVERLAY
  useEffect(() => {
    if (isOpen && currentOverlay && searchAddress?.geometry?.rings) {
      fetchOverlayData(currentOverlay.overlayId);
    }
  }, [isOpen, currentOverlay, searchAddress]);

  const fetchOverlayData = async (overlayId) => {
    setLoading(true);
    setError('');

    try {
      const data = await getOverlayByAddressCoordinates({
        overlayId,
        geoJsonCode,
      });

      if (data) {
        setCurrentOverlayData({ ...data, overlayId });
      } else {
        setError('Unable to fetch overlay data');
      }
    } catch (err) {
      setError('Failed to load overlay data');
    } finally {
      setLoading(false);
    }
  };

  // 🔥 MAIN CAPTURE FUNCTION (FIXED)
  const captureScreenshot = useCallback(
    async (overlayToCapture) => {
      if (!captureScreenshotFn.current) {
        setError('Map is not ready yet...');
        return;
      }

      if (isCapturing) return;

      setIsCapturing(true);
      setError('');

      try {
        const base64Screenshot = await captureScreenshotFn.current();

        if (!base64Screenshot) {
          throw new Error('Empty screenshot');
        }

        setCapturedScreenshots((prev) => ({
          ...prev,
          [overlayToCapture.overlayName]: base64Screenshot,
        }));

        setSuccessMessage(`✓ ${overlayToCapture.overlayName} captured!`);

        setTimeout(() => setSuccessMessage(''), 1200);

        // 🔥 MOVE TO NEXT
        setCurrentOverlayData(null);
      } catch (err) {
        console.error(err);
        setError(`Failed to capture ${overlayToCapture.overlayName}`);
      } finally {
        setIsCapturing(false); // 🔥 ALWAYS RESET
      }
    },
    [isCapturing]
  );

  // 🔥 RESET capture state when overlay changes
  useEffect(() => {
    setIsCapturing(false);
  }, [currentOverlayData]);

  // 🔥 GENERATE REPORT
  const handleGenerateReport = async () => {
    if (!capturedCount) {
      setError('No screenshots available');
      return;
    }

    setIsGeneratingReport(true);
    setError('');

    try {
      const screenshotsArray = Object.entries(capturedScreenshots).map(([name, screenshot]) => ({
        name,
        screenshot,
      }));

      const result = await generateReport({
        title: 'Overlay Analysis Report',
        address: searchAddress?.address || 'Unknown Address',
        screenshots: screenshotsArray,
      });

      if (result.success) {
        setSuccessMessage(`✓ Report generated (${result.fileName})`);

        onComplete?.(true, {
          screenshots: capturedScreenshots,
          fileName: result.fileName,
        });
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsGeneratingReport(false);
      onClose();
    }
  };

  const handleManualCapture = () => {
    captureScreenshot(currentOverlay);
  };

  const handleClose = () => {
    if (isCapturing || isGeneratingReport) return;
    clearAllMemory();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            Capture Overlay Screenshots{' '}
            <span style={{ fontSize: '14px' }}>
              (Stay on this page, switching tabs may cause black screenshots.)
            </span>
          </ModalTitle>
          {/* {allCompleted && (
            <CloseButton onClick={handleClose}>
              <X size={24} />
            </CloseButton>
          )} */}
        </ModalHeader>

        <ModalBody>
          <MapContainer>
            {loading && <ModalLoadingScreen />}

            {!loading && currentOverlayData && !allCompleted && (
              <ArcgisMap
                key={currentOverlay?.overlayName}
                address={searchAddress.address}
                cords={searchAddress.geometry?.rings}
                bushfireData={[currentOverlayData]}
                isMultiScreenshotMode={true}
                onScreenshotCaptured={(fn) => {
                  captureScreenshotFn.current = fn;

                  // 🔥 AUTO CAPTURE (NO POLLING)
                  if (!isCapturing && !isGeneratingReport) {
                    captureScreenshot(currentOverlay);
                  }
                }}
              />
            )}

            {allCompleted && (
              <CurrentOverlaySection>
                <OverlayLabel style={{ textAlign: 'center' }}>
                  ✓ All overlays captured!
                </OverlayLabel>
                <OverlayName style={{ textAlign: 'center' }}>
                  Click &quot;Generate Report&quot; to download your Word document.
                </OverlayName>
              </CurrentOverlaySection>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
          </MapContainer>

          <Sidebar>
            <ProgressSection>
              <ProgressBar>
                <ProgressFill progress={progress} />
              </ProgressBar>
              <ProgressText>
                {capturedCount} of {totalOverlays} completed
              </ProgressText>
            </ProgressSection>

            {currentOverlay && !allCompleted && (
              <CurrentOverlaySection>
                <OverlayLabel>Current</OverlayLabel>
                <OverlayName>{currentOverlay.overlayName}</OverlayName>
              </CurrentOverlaySection>
            )}

            {capturedCount > 0 && (
              <CompletedList>
                <OverlayLabel style={{ marginBottom: '12px' }}>Completed</OverlayLabel>
                {Object.keys(capturedScreenshots).map((name, i) => (
                  <CompletedItem key={i}>
                    <CheckIcon size={18} />
                    <CompletedItemText>{name}</CompletedItemText>
                  </CompletedItem>
                ))}
              </CompletedList>
            )}

            <ButtonContainer>
              {!allCompleted && (
                <CaptureButton onClick={handleManualCapture} disabled={isCapturing || loading}>
                  {isCapturing ? 'Capturing...' : 'Capture Manually'}
                </CaptureButton>
              )}

              {allCompleted && (
                <CompleteButton onClick={handleGenerateReport}>
                  <Check size={18} />
                  Generate Report
                </CompleteButton>
              )}
            </ButtonContainer>
          </Sidebar>
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default NewMultipleScreenshotModal;
